import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CuttysarkDivision,
  CuttysarkDivisionResult,
  CuttysarkLiveCandidate,
} from '@cortex/domain-product-innovation-lab-types';
import { podiumOf, type RevealFocus } from './stage-helpers.js';
import { StagePhoto } from './StageCandidates.js';

/** 「第 N 位」の札を出しておく時間（ミリ秒）。 @graph-connects none */
export const RANK_TITLE_MS = 2500;
/** 2・3 位のドラムロールの長さ（ミリ秒）。 @graph-connects none */
export const DRUMROLL_MS = 3000;
/** 1 位のドラムロールの長さ（ミリ秒）。溜めを長くして特別感を出す。 @graph-connects none */
export const CHAMPION_DRUMROLL_MS = 6000;

/** 同順位の人数ごとの大きさ（写真・名前・二つ名・光の放射・1 人の幅） @graph-connects none */
interface TieLayout {
  photo: string;
  name: string;
  title: string;
  rays: string;
  width: string;
}

/**
 * 同順位の人数に応じた大きさ。同率で何人並んでも 1 画面に横一列（多ければ折り返し）で収まるよう、
 * 人数が増えるほど小さくする。1 人のときだけ 1 位をさらに大きくする。
 * @graph-connects none
 */
export function tieLayout(count: number, champion: boolean): TieLayout {
  if (count <= 1) {
    return {
      photo: champion ? 'h-[34vh] w-[34vh]' : 'h-[28vh] w-[28vh]',
      name: 'text-[clamp(2.2rem,6vw,5rem)]',
      title: 'text-[clamp(1rem,2.2vw,1.8rem)]',
      rays: 'h-[50vh] w-[50vh]',
      width: 'max-w-[60vw]',
    };
  }
  if (count === 2) {
    return {
      photo: 'h-[26vh] w-[26vh]',
      name: 'text-[clamp(1.8rem,4.6vw,4rem)]',
      title: 'text-[clamp(0.9rem,1.8vw,1.5rem)]',
      rays: 'h-[40vh] w-[40vh]',
      width: 'max-w-[40vw]',
    };
  }
  if (count === 3) {
    return {
      photo: 'h-[22vh] w-[22vh]',
      name: 'text-[clamp(1.6rem,3.6vw,3.2rem)]',
      title: 'text-[clamp(0.85rem,1.5vw,1.3rem)]',
      rays: 'h-[34vh] w-[34vh]',
      width: 'max-w-[28vw]',
    };
  }
  return {
    photo: 'h-[15vh] w-[15vh]',
    name: 'text-[clamp(1.2rem,2.6vw,2.2rem)]',
    title: 'text-[clamp(0.75rem,1.2vw,1.05rem)]',
    rays: 'h-[24vh] w-[24vh]',
    width: 'max-w-[20vw]',
  };
}

/** 発表の段取り（札 → ドラムロール → 発表） @graph-connects none */
export type RevealStepName = 'title' | 'drumroll' | 'revealed';

/** 発表の props @graph-connects none */
interface RankRevealProps {
  focus: RevealFocus;
  divisionName: string;
  candidates: CuttysarkLiveCandidate[];
  /** 札から順に見せるか。画面を途中で開いた・読み直したときは発表済みの状態から出す。 */
  animate: boolean;
  /**
   * 段取りが進んだときに呼ぶ（効果音・紙吹雪・コメントは画面側で出す）。animated は札から見せたか
   * （途中で開いた画面では、発表済みの状態から出すので効果音を鳴らさない）。
   */
  onStep: (step: RevealStepName, info: { champion: boolean; animated: boolean }) => void;
}

/**
 * 1 つの順位の発表。「第 N 位」の札 → ドラムロール → 発表と自動で進める（運営は順位を進めるだけ）。
 * 1 位は溜めを長く・文字を大きくし、写真の後ろに光の放射を回す（暗転・スポットライト・閃光は画面全体に
 * かけるので、onStep を受けた画面側で出す）。呼び出し側が部門 + 順位を key にするので、
 * 順位が替わるたびに札からやり直す。ポーリングで props が変わっても段取りは止めない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面の順位発表（第 N 位の札・ドラムロール・発表。1 位は豪華に）
 * 同順位の人は「同率第 N 位」として集計結果の並び順に横へ並べ、人数に応じて小さくする
 * （札とドラムロールでは同率かどうかを伏せ、発表で初めて出す）。
 * @graph-connects StagePhoto [calls] 候補者写真
 * @graph-connects tieLayout [calls] 同順位の人数ごとの大きさ
 */
export function RankReveal({
  focus,
  divisionName,
  candidates,
  animate,
  onStep,
}: RankRevealProps): ReactNode {
  const { t } = useTranslation();
  const champion = focus.rank === 1;
  const [step, setStep] = useState<RevealStepName>(animate ? 'title' : 'revealed');
  // 札から見せたかは最初の描画で決まる（以降のポーリングで変わらない）
  const [animated] = useState(animate);
  // onStep は親の再描画ごとに作り直されるので、段取りのタイマーを張り直さないよう ref で持つ
  const onStepRef = useRef(onStep);
  useEffect(() => {
    onStepRef.current = onStep;
  }, [onStep]);

  useEffect(() => {
    onStepRef.current(step, { champion, animated });
    if (step === 'revealed') return;
    const ms = step === 'title' ? RANK_TITLE_MS : champion ? CHAMPION_DRUMROLL_MS : DRUMROLL_MS;
    const timer = setTimeout(() => {
      setStep(step === 'title' ? 'drumroll' : 'revealed');
    }, ms);
    return () => {
      clearTimeout(timer);
    };
  }, [step, champion, animated]);

  const rankLabel = t('cuttysark.stageScreen.rank', { rank: focus.rank });

  if (step === 'title') {
    return (
      <div className="flex flex-col items-center gap-[3vh] text-center">
        <div className="cs-slide-up text-[clamp(1.2rem,2.6vw,2.2rem)] font-bold tracking-[0.4em] text-blue-200">
          {divisionName}
        </div>
        <div
          className={`cs-zoom-in font-black text-amber-300 [text-shadow:0_0_40px_rgb(250_204_21/0.7)] ${
            champion ? 'text-[clamp(6rem,22vw,18rem)]' : 'text-[clamp(5rem,16vw,13rem)]'
          }`}
        >
          {rankLabel}
        </div>
      </div>
    );
  }

  if (step === 'drumroll') {
    return (
      <div className="relative flex flex-col items-center gap-[4vh] text-center">
        <div className="text-[clamp(1.2rem,2.6vw,2.2rem)] font-bold tracking-[0.4em] text-blue-200">
          {divisionName}
        </div>
        <div
          className={`cs-drumroll font-black text-amber-300 ${
            champion
              ? 'cs-swell text-[clamp(4rem,14vw,11rem)] [text-shadow:0_0_60px_rgb(250_204_21/0.9)]'
              : 'text-[clamp(3rem,10vw,8rem)]'
          }`}
        >
          {t('cuttysark.stageScreen.drumroll', { rank: focus.rank })}
        </div>
        <div className="cs-shimmer h-[1.2vh] w-[50vw] rounded-full" />
      </div>
    );
  }

  const tied = focus.entries.length > 1;
  const layout = tieLayout(focus.entries.length, champion);
  return (
    <div className="relative flex w-full flex-col items-center gap-[3vh] px-[4vw] text-center">
      <div className="text-[clamp(1.2rem,2.6vw,2.2rem)] font-bold tracking-[0.4em] text-blue-200">
        {divisionName}
      </div>
      <div
        className={`cs-pop font-black text-amber-300 [text-shadow:0_0_32px_rgb(250_204_21/0.6)] ${
          champion ? 'text-[clamp(3rem,8vw,6.5rem)]' : 'text-[clamp(2.4rem,6vw,5rem)]'
        }`}
      >
        {tied ? t('cuttysark.stageScreen.tiedRank', { rank: focus.rank }) : rankLabel}
      </div>
      <div className="flex flex-wrap items-start justify-center gap-x-[4vw] gap-y-[3vh]">
        {focus.entries.map((entry, i) => {
          const candidate = candidates.find((c) => c.email === entry.candidateEmail);
          return (
            <div
              key={entry.candidateEmail}
              className={`cs-pop relative flex ${layout.width} flex-col items-center gap-[1.2vh]`}
              // 同率は左から順に少しずつずらして出す
              style={{ animationDelay: `${String(200 + i * 250)}ms` }}
            >
              <div className="relative flex items-center justify-center">
                {champion && (
                  <div
                    aria-hidden="true"
                    className={`cs-rays absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${layout.rays}`}
                  />
                )}
                {candidate && (
                  <StagePhoto candidate={candidate} sizeClass={layout.photo} className="relative" />
                )}
              </div>
              {candidate !== undefined && candidate.title !== '' && (
                <div className={`relative font-bold text-amber-200 ${layout.title}`}>
                  {candidate.title}
                </div>
              )}
              <div className={`relative font-black leading-none text-white ${layout.name}`}>
                {candidate?.name ?? entry.candidateEmail}
              </div>
              <div className="relative flex items-center gap-3">
                <span className="text-[clamp(1.2rem,2.4vw,2rem)] font-bold text-white">
                  {t('cuttysark.stageScreen.votes', { count: entry.votes })}
                </span>
                {entry.tiedAtBoundary ? (
                  <span className="text-[clamp(0.9rem,1.6vw,1.3rem)] font-semibold text-orange-300">
                    {t('cuttysark.stageScreen.tied')}
                  </span>
                ) : (
                  entry.advancing && (
                    <span className="rounded-full bg-amber-400 px-4 text-[clamp(0.9rem,1.6vw,1.3rem)] font-bold text-blue-950">
                      {t('cuttysark.stageScreen.advancing')}
                    </span>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** 表彰台の props @graph-connects none */
interface WinnersPodiumProps {
  divisions: CuttysarkDivision[];
  candidates: CuttysarkLiveCandidate[];
  results: CuttysarkDivisionResult[];
}

/**
 * 終了後の表彰。部門ごとに勝ち上がった人を順位の高い順に大きく並べる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面の表彰（部門ごとの勝ち上がり）
 * @graph-connects podiumOf [calls] 勝ち上がりの並び
 * @graph-connects StagePhoto [calls] 候補者写真
 */
export function WinnersPodium({ divisions, candidates, results }: WinnersPodiumProps): ReactNode {
  const { t } = useTranslation();
  return (
    // 部門は横に並べる（縦に積むと 2 部門で画面の高さを超えるため）。狭い画面だけ縦に積む
    <div className="grid w-full gap-[4vh] px-[4vw] md:auto-cols-fr md:grid-flow-col md:gap-[3vw]">
      {divisions.map((division) => {
        const result = results.find((r) => r.divisionId === division.id);
        if (!result) return null;
        const podium = podiumOf(result, candidates);
        if (podium.length === 0) return null;
        return (
          <section key={division.id} className="flex flex-col items-center gap-[2vh]">
            <h2 className="text-[clamp(1rem,3vh,1.8rem)] font-bold tracking-[0.3em] text-amber-300">
              {division.name}
            </h2>
            <div className="flex flex-wrap items-end justify-center gap-x-[3vw] gap-y-[2vh]">
              {podium.map(({ entry, candidate }, i) => (
                <div
                  key={entry.candidateEmail}
                  className="cs-pop flex flex-col items-center gap-2 text-center"
                  style={{ animationDelay: `${String(i * 250)}ms` }}
                >
                  {candidate && (
                    <StagePhoto
                      candidate={candidate}
                      sizeClass={entry.rank === 1 ? 'h-[20vh] w-[20vh]' : 'h-[14vh] w-[14vh]'}
                    />
                  )}
                  {candidate !== undefined && candidate.title !== '' && (
                    <div className="max-w-[40vw] text-[clamp(0.85rem,2.2vh,1.3rem)] font-semibold text-amber-200">
                      {candidate.title}
                    </div>
                  )}
                  <div className="text-[clamp(1.4rem,4.5vh,3rem)] font-black leading-tight text-white">
                    {candidate?.name ?? entry.candidateEmail}
                  </div>
                  <div className="text-[clamp(0.85rem,2.2vh,1.3rem)] text-blue-200">
                    {t('cuttysark.stageScreen.rank', { rank: entry.rank })} ・{' '}
                    {t('cuttysark.stageScreen.votes', { count: entry.votes })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
